import loadjs from 'loadjs';
import * as Sentry from '@sentry/react';
import { serializeError } from 'serialize-error';
import { logger } from 'utils/logger';
import { BLUR_REVERSE_MAP } from 'features/virtual-backgrounds/blurValueMap';
import {
  VirtualBackgroundProvider,
  BackgroundConfig,
  ActivateBackgroundOptions,
  InputMediaOptions,
} from './VirtualBackgroundProvider';

const customerKey = '12cba3ee81cf4a793796a51b6327c678';
const figmentBundleId = 'figment';

const VB_LOG = { system: true, capture: 'virtual-background' } as const;

interface FigmentActivationConfig {
  activeApp: 'VB' | 'AR';
  background: {
    type: string;
    bgImageUrl?: string;
    bgVideoUrl?: string;
    blurStrength?: string;
  };
  inputFrequency?: number;
  inputMediaStream: {
    mediaStream?: MediaStream;
    videoTrack?: MediaStreamTrack;
    stopTracksOnDestruction: boolean;
  };
  vbQuality?: 'performance' | 'quality';
}

export class FigmentProvider extends VirtualBackgroundProvider {
  private _instance: any;

  private _activated = false;

  private _activeBackground: BackgroundConfig = { type: '', value: '' };

  /** True when resetActivation() was called — SDK needs deactivation before next activate. */
  private _needsDeactivation = false;

  get name() {
    return 'figment';
  }

  get activated() {
    return this._activated;
  }

  get activeBackground() {
    return this._activeBackground;
  }

  get initialized() {
    return !!this._instance;
  }

  private loadScripts = async () =>
    loadjs(
      [
        `${process.env.PUBLIC_URL}/figment/figment.js`,
        `${process.env.PUBLIC_URL}/figment/vb/tflite/figment-021fg1a9.js`,
        `${process.env.PUBLIC_URL}/figment/vb/tflite/figment-a19j11h7.js`,
      ],
      figmentBundleId,
      { returnPromise: true }
    );

  /** Maps internal numeric blur value to Figment's named string */
  private toFigmentBlurValue(value: string): string {
    return BLUR_REVERSE_MAP[value] ?? value;
  }

  async init(): Promise<void> {
    if (this._instance) {
      return;
    }

    if (!loadjs.isDefined(figmentBundleId)) {
      await this.loadScripts();
    }

    this._instance = new (window as any).FigmentApp({ customerKey });
  }

  async activate(options: ActivateBackgroundOptions): Promise<MediaStream | void> {
    if (this._activated) {
      return undefined;
    }

    // Deactivate it before re-activating to ensure clean state.
    if (this._needsDeactivation) {
      this._needsDeactivation = false;
      try {
        await this._instance.deactivate();
      } catch {
        /* non-fatal */
      }
    }

    const config: FigmentActivationConfig = {
      activeApp: 'VB',
      background: { type: options.type },
      inputFrequency: 1000 / 30,
      vbQuality: 'quality',
      inputMediaStream: {
        mediaStream: options.stream,
        stopTracksOnDestruction: true,
      },
    };

    if (options.type === 'blur') {
      config.background.blurStrength = this.toFigmentBlurValue(options.value);
    }
    if (options.type === 'image') {
      config.background.bgImageUrl = options.value;
    }
    if (options.type === 'video') {
      config.background.bgVideoUrl = options.value;
    }

    const stream = await this._instance.activate(config);
    if (!stream) {
      return undefined;
    }
    this._activated = true;
    this._activeBackground = { type: options.type, value: options.value };
    return stream;
  }

  async deactivate(): Promise<void> {
    if (!this._activated) {
      return;
    }

    try {
      await this._instance.deactivate();
      this._activeBackground = { type: '', value: '' };
    } catch {
      // Figment deactivation errors are non-fatal (matches existing behavior)
    } finally {
      this._activated = false;
    }
  }

  async changeBackground(config: BackgroundConfig): Promise<void> {
    try {
      if (config.type === 'blur') {
        await this._instance.setOption('blur_background', this.toFigmentBlurValue(config.value));
      }
      if (config.type === 'image') {
        await this._instance.setOption('set_background_img', config.value);
      }
      if (config.type === 'video') {
        await this._instance.setOption('set_background_video', config.value);
      }

      this._activeBackground = { type: config.type, value: config.value };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'FigmentProvider', operation: 'changeBackground' },
      });
      logger.remote(VB_LOG).error('[VB] Figment background change failed', serializeError(error));
      throw error;
    }
  }

  async setInputMedia(options: InputMediaOptions): Promise<MediaStream> {
    return this._instance.setInputMediaStream(
      options.stream,
      options.stopTracksOnDestruction ?? true
    );
  }

  reset(): void {
    if (!this._activated) {
      return;
    }
    this._activated = false;
    this._activeBackground = { type: '', value: '' };
    this._needsDeactivation = true;
  }

  async destroy(): Promise<void> {
    try {
      if (this._activated) {
        await this.deactivate();
      }
    } finally {
      this._needsDeactivation = false;
      this._instance = null;
    }
  }
}
